// EvsiqCleaningLogCard — an account manager enters a room's cleaning log in EVSiQ from what the
// cleaner sent them (Ben, 2026-10-27: write path A; date and time; the cleaner named with their sub;
// photos OPTIONAL — a photo is never required to log a clean).
//
// Shown only on a building that has rooms on EVSiQ, and only to the field seats; the EVSiQ function
// asks the TCS database whether this person may log at this building and refuses everyone else.
// A load error is shown, never swallowed into an empty card (STATE, decision 79).
import { useEffect, useState } from 'react';
import { ClipboardCheck, X } from 'lucide-react';
import { Button } from '../../../shared/components';
import { useAuth } from '../../../shared/auth';
import { isFieldSeat } from '../../../shared/role-gates';
import { overlayClickClose } from '../../../shared/overlay-close';
import { errorMessage } from '../../../shared/errors';
import {
  cleanerLabel, fetchEvsiqOptions, fileToBase64, localInputToIso, saveManualLog,
  type EvsiqOptions, type ManualLogSaved,
} from '../lib/evsiq-manual-log';

const label = 'text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted';
const field = 'mt-1 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none';

export function EvsiqCleaningLogCard({ facilityId, buildingLabel }: { facilityId: string; buildingLabel: string }) {
  const { profile } = useAuth();
  const allowed = isFieldSeat(profile);
  const [opts, setOpts] = useState<EvsiqOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    fetchEvsiqOptions(facilityId)
      .then((o) => { if (alive) { setOpts(o); setLoadError(null); } })
      .catch((e) => { if (alive) setLoadError(errorMessage(e, 'EVSiQ could not be reached.')); });
    return () => { alive = false; };
  }, [allowed, facilityId, open]);

  if (!allowed) return null;
  if (loadError) {
    return (
      <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4 text-sm text-tcs-text-muted" data-testid="evsiq-log-card-error">
        Cleaning log (EVSiQ): {loadError}
      </div>
    );
  }
  if (!opts?.on_evsiq) return null;

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5" data-testid="evsiq-log-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
            <ClipboardCheck className="h-4 w-4" /> Cleaning log (EVSiQ)
          </h3>
          <p className="mt-1 text-xs text-tcs-text-muted">
            When a cleaner could not log a room themselves, enter it here from what they sent you. It shows on
            the building's cleaning log as entered by you.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)} data-testid="evsiq-log-open">
          Enter a cleaning log
        </Button>
      </div>
      {open && (
        <EvsiqLogModal
          facilityId={facilityId}
          // The building named with its cleaner (standing rule): "Surgicenter Of Kansas City (Shiny Floors …)".
          buildingLabel={withSubs(buildingLabel, opts)}
          opts={opts}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function withSubs(name: string, opts: EvsiqOptions): string {
  const subs = [...new Set((opts.cleaners ?? []).map((c) => c.sub_name).filter(Boolean))];
  return subs.length ? `${name} (${subs.join(', ')})` : name;
}

function EvsiqLogModal({ facilityId, buildingLabel, opts, onClose }: {
  facilityId: string; buildingLabel: string; opts: EvsiqOptions; onClose: () => void;
}) {
  const { profile } = useAuth();
  const [roomId, setRoomId] = useState('');
  const [when, setWhen] = useState('');
  const [cleanerId, setCleanerId] = useState(opts.cleaners?.length === 1 ? opts.cleaners[0].uuid : '');
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ManualLogSaved | null>(null);
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  async function save() {
    if (!roomId) return setError('Pick the room.');
    const iso = localInputToIso(when);
    if (!iso) return setError('Give the date and time it was cleaned.');
    if (Date.parse(iso) > Date.now()) return setError('That time is in the future. Give the date and time it was cleaned.');
    if (!cleanerId) return setError('Pick the cleaner who did it.');
    setSaving(true);
    setError(null);
    try {
      const photos = await Promise.all(files.map(async (f) => ({ base64: await fileToBase64(f), mime: f.type })));
      setDone(await saveManualLog({ facility_id: facilityId, room_id: roomId, cleaned_by: cleanerId, logged_at: iso, notes: notes.trim() || null, photos }));
    } catch (e) {
      setError(errorMessage(e, 'The log could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4" {...overlayClickClose(onClose)}>
      <div className="w-full max-w-md rounded-xl border border-tcs-border bg-tcs-overlay p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-tcs-text">Enter a cleaning log</h3>
            <p className="mt-1 text-xs text-tcs-text-muted">{buildingLabel}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        {done ? (
          <div className="mt-4 space-y-3" data-testid="evsiq-log-done">
            <p className="rounded bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg-strong">
              Saved. {done.room}, cleaned by {done.sub_name ? `${done.cleaner} (${done.sub_name})` : done.cleaner}, logged{' '}
              {new Date(done.logged_at).toLocaleString()}. Entered by {profile?.display_name ?? 'you'}
              {done.photos > 0 ? ` with ${done.photos} photo${done.photos === 1 ? '' : 's'}` : ''}.
            </p>
            <p className="text-xs text-tcs-text-muted">It cannot be changed by a cleaner or the kiosk.</p>
            <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor="evl-room" className={label}>Room</label>
              <select id="evl-room" value={roomId} onChange={(e) => setRoomId(e.target.value)} className={field}>
                <option value="">Pick a room</option>
                {(opts.rooms ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="evl-when" className={label}>Date and time cleaned ({zone})</label>
              <input id="evl-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={field} />
            </div>
            <div>
              <label htmlFor="evl-cleaner" className={label}>Cleaner</label>
              <select id="evl-cleaner" value={cleanerId} onChange={(e) => setCleanerId(e.target.value)} className={field}>
                <option value="">Pick the cleaner</option>
                {(opts.cleaners ?? []).map((c) => <option key={c.uuid} value={c.uuid}>{cleanerLabel(c)}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="evl-photos" className={label}>Photos (optional)</label>
              <input id="evl-photos" type="file" accept="image/jpeg,image/png,image/heic,image/heif" multiple
                onChange={(e) => setFiles(Array.from(e.target.files ?? []))} className="mt-1 block w-full text-sm text-tcs-text" />
            </div>
            <div>
              <label htmlFor="evl-notes" className={label}>Note (optional)</label>
              <textarea id="evl-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={field} />
            </div>
            {error && <p className="rounded bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong" data-testid="evsiq-log-error">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={saving} onClick={() => void save()} data-testid="evsiq-log-save">
                {saving ? 'Saving…' : 'Save the log'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
